import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, CheckCircle, Shield, Clock, ArrowRight } from 'lucide-react';
import { dataQualityService } from '../../services/dataQualityService';

const DashboardDataQualityWidget = () => {
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await dataQualityService.dashboard.get();
                if (response.success && response.data) {
                    setDashboard(response.data);
                }
            } catch (err) {
                console.error('Error fetching data quality widget:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const formatNumber = (num) => {
        if (num === null || num === undefined) return '0';
        return new Intl.NumberFormat().format(num);
    };

    const formatPercentage = (num) => {
        if (num === null || num === undefined) return '0%';
        return `${Number(num).toFixed(1)}%`;
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="animate-pulse">
                    <div className="h-5 bg-gray-200 rounded w-1/3 mb-4"></div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="h-20 bg-gray-100 rounded-lg"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (!dashboard) return null;

    const avgCompleteness = dashboard.retailCompleteness && dashboard.corporateCompleteness
        ? ((dashboard.retailCompleteness.averageCompleteness + dashboard.corporateCompleteness.averageCompleteness) / 2)
        : dashboard.retailCompleteness?.averageCompleteness || dashboard.corporateCompleteness?.averageCompleteness || 0;

    const cards = [
        {
            label: 'Duplicate Groups',
            value: formatNumber(dashboard.potentialDuplicateGroups),
            icon: Copy,
            color: 'text-red-600 bg-red-50',
        },
        {
            label: 'Avg Completeness',
            value: formatPercentage(avgCompleteness),
            icon: CheckCircle,
            color: 'text-green-600 bg-green-50',
        },
        {
            label: 'KYC Approval Rate',
            value: formatPercentage(dashboard.kycHealth?.kycApprovalRate),
            icon: Shield,
            color: 'text-purple-600 bg-purple-50',
        },
        {
            label: 'Stale Records',
            value: formatNumber(dashboard.clientHealth?.staleClients),
            icon: Clock,
            color: 'text-orange-600 bg-orange-50',
        },
    ];

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Data Quality
                </h3>
                <button
                    onClick={() => navigate('/data-quality/dashboard')}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                >
                    View Details <ArrowRight className="w-4 h-4" />
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {cards.map((card) => (
                    <div key={card.label} className={`rounded-lg p-4 ${card.color}`}>
                        <div className="flex items-center gap-2 mb-1">
                            <card.icon className="w-4 h-4" />
                            <p className="text-sm font-medium">{card.label}</p>
                        </div>
                        <p className="text-lg font-semibold">{card.value}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DashboardDataQualityWidget;
